import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TileItem, TileSelectionProps } from '../entities/TileType';
import { SpinnerComponent } from './SpinnerComponent';
import './TileSelectionComponent.css';

const SELECTED_SERVICE_KEY = 'selectedService';

type ComponentState = 'loading' | 'error' | 'empty' | 'ready';

export interface SelectedService {
    serviceId: string | number;
    serviceName: string;
}

const saveToSessionStorage = (item: TileItem): void => {
    const serviceData: SelectedService = {
        serviceId: item.id,
        serviceName: item.name
    };
    sessionStorage.setItem(SELECTED_SERVICE_KEY, JSON.stringify(serviceData));
};

export const getSelectedService = (): SelectedService | null => {
    const data = sessionStorage.getItem(SELECTED_SERVICE_KEY);
    if (data) {
        try {
            return JSON.parse(data) as SelectedService;
        } catch {
            return null;
        }
    }
    return null;
};

export const clearSelectedService = (): void => {
    sessionStorage.removeItem(SELECTED_SERVICE_KEY);
};

export const replaceTitlePlaceholders = (title: string): string => {
    if (!title) return title;
    const service = getSelectedService();
    if (service) {
        title = title.replace(/\{\{serviceName\}\}/gi, service.serviceName);
        title = title.replace(/\{\{serviceId\}\}/gi, String(service.serviceId));
    }
    return title;
};

const combineUrl = (base?: string, path?: string): string => {
    if (!path) return base || '';
    if (!base) return path;
    // If path already starts with http/https, just return it
    if (/^https?:\/\//i.test(path)) {
        return path;
    }
    // Ensure only one slash between base and path
    return base.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
};

const TileSelectionComponent: React.FC<TileSelectionProps> = ({
    apiUrl,
    baseUrl,
    responseKey = 'services',
    idField = 'id',
    nameField = 'name',
    title,
    onSelect,
    onAutoSelect,
    authToken,
    isEng = true,
    autoSelectSingle = true
}) => {
    const [state, setState] = useState<ComponentState>('loading');
    const [items, setItems] = useState<TileItem[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const displayTitle = title || null;

    useEffect(() => {
        if (authToken) {
            fetchData();
        }
    }, [apiUrl, authToken]);

    const fetchData = async () => {
        setState('loading');
        setErrorMessage('');

        try {
            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };

            if (authToken) {
                headers['Authorization'] = `Bearer ${authToken}`;
            }

            const fullUrl = combineUrl(baseUrl, apiUrl);
            const response = await axios.get(fullUrl, { headers });

            if (response.status === 200 && response.data) {
                const rawItems = response.data[responseKey];

                if (!Array.isArray(rawItems)) {
                    console.error('TileSelectionComponent: Response key not found or not an array', responseKey);
                    setErrorMessage(isEng
                        ? 'Invalid response format.'
                        : 'Format de réponse invalide.');
                    setState('error');
                    return;
                }

                const mappedItems: TileItem[] = rawItems.map((item: any) => ({
                    id: item[idField],
                    name: item[nameField],
                    rawData: item
                }));

                if (mappedItems.length === 0) {
                    setState('empty');
                    return;
                }

                if (mappedItems.length === 1 && autoSelectSingle) {
                    saveToSessionStorage(mappedItems[0]);
                    if (onAutoSelect) {
                        onAutoSelect(mappedItems[0]);
                    } else {
                        onSelect(mappedItems[0]);
                    }
                    return;
                }

                setItems(mappedItems);
                setState('ready');
            } else {
                console.error('TileSelectionComponent: Unexpected response', response);
                setErrorMessage(isEng
                    ? 'Failed to load services.'
                    : 'Échec du chargement des services.');
                setState('error');
            }
        } catch (error: any) {
            console.error('TileSelectionComponent: API error', error.message);

            if (error.response?.status === 401) {
                setErrorMessage(isEng
                    ? 'Session expired. Please log in again.'
                    : 'Session expirée. Veuillez vous reconnecter.');
            } else {
                setErrorMessage(isEng
                    ? 'Failed to load services. Please try again.'
                    : 'Échec du chargement des services. Veuillez réessayer.');
            }
            setState('error');
        }
    };

    const handleClick = (item: TileItem) => {
        saveToSessionStorage(item);
        onSelect(item);
    };

    const handleKeyDown = (event: React.KeyboardEvent, item: TileItem) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelect(item);
        }
    };

    const handleRetry = () => {
        fetchData();
    };

    if (state === 'loading') {
        return (
            <div className="tile-selection-loading">
                <SpinnerComponent isBusy={true} />
            </div>
        );
    }

    if (state === 'error') {
        return (
            <div className="tile-selection-container">
                <div className="ontario-alert ontario-alert--error tile-selection-error" role="alert">
                    <div className="ontario-alert__body">
                        <p>{errorMessage}</p>
                    </div>
                </div>
                <button
                    onClick={handleRetry}
                    className="ontario-button ontario-button--primary"
                >
                    {isEng ? 'Try Again' : 'Réessayer'}
                </button>
            </div>
        );
    }

    if (state === 'empty') {
        return (
            <div className="tile-selection-container">
                <div className="tile-selection-empty">
                    {isEng
                        ? 'No services found for your account.'
                        : 'Aucun service trouvé pour votre compte.'}
                </div>
            </div>
        );
    }

    return (
        <div className="tile-selection-container">
            {displayTitle && <h2 className="tile-selection-title">{displayTitle}</h2>}

            <div className="tile-selection-list">
                {items.map((item) => (
                    <div
                        key={item.id}
                        className="tile-selection-item"
                        onClick={() => handleClick(item)}
                        onKeyDown={(e) => handleKeyDown(e, item)}
                        role="button"
                        tabIndex={0}
                        aria-label={item.name}
                    >
                        <span className="tile-selection-item-name">
                            {item.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TileSelectionComponent;
